import { useState, useEffect, useCallback } from 'react';
import { ApiClient } from '@center-technologies/shared-api-clients';
import { useApiClientsOrNull } from '../context/ApiClientsContext';
import type { PublisherPermissions } from 'types/publisher';
import { getPublisher } from '../api/marketplaceApi';

// Module-scope cache keyed by schemaClient ref so the hook is stable across
// re-renders without triggering extra fetches. Fail-closed: returns null until
// the fetch resolves so the UI never renders privileged controls on a cache miss.
// Only a real answer is cached; an unavailable one is re-asked on the next mount.
let permissionsCache = new WeakMap<ApiClient, Map<string, PublisherPermissions>>();

export function resetMarketplacePermissionsCache(): void {
  permissionsCache = new WeakMap();
}

export interface UseMarketplacePermissionsResult {
  permissions: PublisherPermissions | null;
  /**
   * `true` when the server could not check the caller's permissions (`permissionsUnavailable`):
   * `permissions` is then every flag `false` and is not a denial. Hide the controls, say they
   * could not be checked and offer `refetch`.
   */
  unavailable: boolean;
  /**
   * `true` while there is no answer yet for the current slug (first render, a slug change, a
   * `refetch`). `permissions` is then `null` and is not a denial: show a loading state.
   */
  loading: boolean;
  refetch: () => void;
}

/** One settled answer, tied to the slug it was fetched for so it never answers for another. */
interface PermissionsAnswer {
  slug: string;
  permissions: PublisherPermissions | null;
  unavailable: boolean;
}

export function useMarketplacePermissions(
  slug: string | null
): UseMarketplacePermissionsResult {
  const apiClients = useApiClientsOrNull();
  const schemaClient = apiClients?.schemaClient ?? null;

  const [answer, setAnswer] = useState<PermissionsAnswer | null>(null);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const refetch = useCallback(() => {
    if (schemaClient && slug) {
      permissionsCache.get(schemaClient)?.delete(slug);
    }
    setAnswer(null);
    setFetchTrigger((prev) => prev + 1);
  }, [schemaClient, slug]);

  useEffect(() => {
    if (!schemaClient || !slug) {
      setAnswer(null);
      return;
    }

    const cached = permissionsCache.get(schemaClient)?.get(slug);
    if (cached) {
      setAnswer({ slug, permissions: cached, unavailable: false });
      return;
    }

    let cancelled = false;

    getPublisher(schemaClient, slug)
      .then((res) => {
        if (cancelled) return;
        const isUnavailable = res.publisher.permissionsUnavailable === true;
        if (!isUnavailable) {
          if (!permissionsCache.has(schemaClient)) {
            permissionsCache.set(schemaClient, new Map());
          }
          permissionsCache.get(schemaClient)!.set(slug, res.publisher.permissions);
        }
        setAnswer({ slug, permissions: res.publisher.permissions, unavailable: isUnavailable });
      })
      .catch(() => {
        // Fail-closed: a settled null on error so no privileged UI is shown
        if (!cancelled) setAnswer({ slug, permissions: null, unavailable: false });
      });

    return () => { cancelled = true; };
  }, [schemaClient, slug, fetchTrigger]);

  if (!schemaClient || !slug) {
    return { permissions: null, unavailable: false, loading: false, refetch };
  }

  if (answer?.slug === slug) {
    return { permissions: answer.permissions, unavailable: answer.unavailable, loading: false, refetch };
  }

  // No answer for this slug yet: a cached real answer serves at once, otherwise it is loading.
  const cached = permissionsCache.get(schemaClient)?.get(slug);
  if (cached) {
    return { permissions: cached, unavailable: false, loading: false, refetch };
  }
  return { permissions: null, unavailable: false, loading: true, refetch };
}
